import { Badge, type BadgeTone } from './Badge';

/**
 * A workflow status, rendered consistently everywhere it appears.
 *
 * TWO RULES, both from the domain rather than from taste:
 *
 * 1. NEVER render a stage `key` to a human. The firm configures a `name` for staff
 *    and a `client_label` for clients, and the portal must use `client_label` only
 *    (Spec §7.1). Pass the label; this component will not invent one from a key.
 *
 * 2. Tone is advisory. The same key means different things in different firms'
 *    workflows, so `tone` is an explicit prop rather than derived from the string.
 *    `statusTone()` below is a convenience for the small set of statuses the KERNEL
 *    defines — case status, payment status, scan status — not for configured stages.
 */

export interface StatusPillProps {
  /** The human-facing label. For a client audience this must be `client_label`. */
  label: string;
  tone?: BadgeTone;
  size?: 'sm' | 'md';
  className?: string;
}

/** Tones for the statuses defined in the backend models, not for configured stages. */
export function statusTone(status: string): BadgeTone {
  switch (status) {
    // Case
    case 'open':
      return 'info';
    case 'awaiting_client':
      return 'warning';
    case 'on_hold':
      return 'warning';
    case 'completed':
      return 'success';
    case 'closed':
      return 'neutral';
    case 'cancelled':
      return 'neutral';
    // Documents
    case 'approved':
      return 'success';
    case 'rejected':
      return 'danger';
    case 'under_review':
      return 'info';
    case 'pending':
      return 'neutral';
    // Malware scanning — `infected` must never read as anything but danger.
    case 'clean':
      return 'success';
    case 'infected':
      return 'danger';
    case 'scanning':
      return 'info';
    case 'failed':
      return 'danger';
    // Finance
    case 'draft':
      return 'neutral';
    case 'issued':
      return 'info';
    case 'part_paid':
      return 'warning';
    case 'paid':
      return 'success';
    case 'void':
      return 'neutral';
    case 'awaiting_verification':
      return 'warning';
    case 'verified':
      return 'success';
    case 'refunded':
      return 'neutral';
    // Critical dates
    case 'unverified':
      return 'warning';
    case 'satisfied':
      return 'success';
    case 'missed':
      return 'danger';
    default:
      return 'neutral';
  }
}

export function StatusPill({ label, tone, size = 'sm', className }: StatusPillProps) {
  return (
    <Badge tone={tone ?? 'neutral'} size={size} dot className={className}>
      {label}
    </Badge>
  );
}
